import $ from 'jquery';
import RadioGroup from './v10/customelements/radio-group.js';
import { v4 as uuidv4 } from '/node_modules/uuid/dist/index.js';

//Calls generic elements event preventDefault and stopPropagation functions,
//this is usefull when testing links analytics. DEBUG should be always false when commiting
//the code.
window.ANALYTIC_DEBUG = false;

/**
 * Class hold most of the all analytic logic in the Laakkonen web page.
 * It bind to click event of generic elements and little more in every page load.
 * Data is pushed to Google analytic's data layer.
 *
 * New analytics is done by this specification:
 * https://github.com/oikioagency/laakkonen/blob/master/dataLayer.js
 */
class Analytics {
  /**
   * Bind all analytics event listeners and initilaizes
   * things for the analytics.
   * @public
   */
  init() {
    this._initUserAnalytics();
    this._bindLiftCard();
    this._bindPhoneNumbers();
    this._bindLekanenChat();
    this._bindMailAddresses();
    this._bindCardownloadPDF();
    this._bindGenericElements();
    //this._bindTalkNowActiveContentBtn();
  }

  /**
   * Format car data to product data object
   * @param {object} carData car data object
   * @returns {object} formatted product data object
   */
  formatCarDataToProductData(carData) {
    return {
      car_brand: carData?.brand_name ?? '',
      car_model: carData?.model_name ?? '',
      car_register_id: carData?.car_id ?? '',
      car_construction_year: carData?.year ?? '',
      car_price: carData.price?.price ?? '',
      car_raw_data: JSON.stringify(carData) ?? '',
      car_body_type: carData?.body_type ?? '',
      car_type: carData?.car_category_name,
      car_outlet: carData?.basicinfo?.name ?? '',
      car_fuelType: carData?.propulsion?.name ?? '',
      car_mileage: carData?.mileage ?? '',
      car_transmission: carData?.gear?.name ?? '',
      car_emission: carData?.co2_emission ?? '',
    }
  }

  /**
   * Push analytic object to data layer
   * @param {string} cartId car id
   * @param {object} carPageAppState carPageAppStage object
   */
  pushAddToCart(cartId, carPageAppState) {
    const carData = carPageAppState.carData;

    this.pushToDataLayer({
      event: 'add_to_cart',
      pageUrl: window.location.href,
      cart_id: cartId,
      product_data: this.formatCarDataToProductData(carData)
    });
  }

  /**
   * Push checkout analytic object to data layer
   * @param {string} cartId car id
   * @param {object[]} products array of car data objects
   */
  pushCheckout( cartId, products ) {
    this.pushToDataLayer({
      event: 'checkout',
      pageUrl: window.location.href,
      cart_id: cartId,
      product_data: products.map( this.formatCarDataToProductData )
    });
  }

  /**
   * Push UI interaction to analytics layer
   * @param {object} data data object include element, elementName and type
   * @param {string|boolean} customEventName - Customized event name
   *
   * @see https://crasman.atlassian.net/browse/LAAKJK-1482?focusedCommentId=97634
   * @public
   */
  pushUIInteractionAnalytics(data, customEventName = false) {
    const uiInteractionObj = {
      event: customEventName || 'UI Interaction',
      interaction: data,
    };

    if (data.action) {
      uiInteractionObj.action = data.action;
      delete uiInteractionObj.interaction.action;
    }

    if (data.productData) {
      uiInteractionObj.productData = data.productData;
      delete uiInteractionObj.interaction.productData;
    }    

    this.pushToDataLayer(uiInteractionObj);
  }

  /**
   * Extract analytic data from the given element object. Null safe, object type is checked to make sure that
   * data can be extracted.
   * @param {Element} el element object implementing Element interface
   * @return {oject} analytic data
   */
  extractAnalyticData(el) {
    if (el instanceof Element) {
      const data = {
        element: el.getAttribute('data-gtm-element'),
        elementName: el.getAttribute('data-gtm-name'),
        type: el.getAttribute('data-gtm-type'),
      };

      if (typeof el.getAttribute('data-gtm-value') !== 'undefined' && el.getAttribute('data-gtm-value') !== null) {
        data.elementValue = el.getAttribute('data-gtm-value');
      }

      return data;
    }
  }

  /**
   * put car search in datalayer
   * @param {string} eventname event name
   */
  pushCarSearchAnalytics(eventname) {
    const carSearch = {};
    const elements = $('a.tag');

    elements.each(index => {
      const element = elements[index];
      const elname = element.dataset.name.replace('[]', '');
      const elvalue = element.dataset.value;

      if (elname === 'km') {
        const kmvalue = $(element)
          .find('span')
          .text();

        if (kmvalue.includes('-')) {
          const splitval = elvalue.split('-');
          carSearch[elname + 'MIN'] = splitval[0];
          carSearch[elname + 'MAX'] = splitval[1];
        } else if (kmvalue.includes('<')) {
          carSearch[elname + 'MAX'] = elvalue;
        } else if (kmvalue.includes('>')) {
          carSearch[elname + 'MIN'] = elvalue;
        }
      } else if (carSearch[elname]) {
        carSearch[elname] = carSearch[elname] + '|' + elvalue;
      } else {
        carSearch[elname] = elvalue;
      }
    });

    const rawDataArray = [];

    Object.entries(carSearch).forEach(([key, value]) => {
      rawDataArray.push(key + ':' + value);
    });

    carSearch.rawData = rawDataArray.join('|');

    this.pushToDataLayer({
      event: eventname,
      carSearch: carSearch,
    });
  }

  /**
   * Push Bavaria car search's analytics to data layer
   * @param {string} eventName event name
   * @param {URLSearchParams} data search
   */
  pushBavariaCarSearchAnalytics(eventName, data) {
    const groups = {};
    const excludedAttributes = ['order'];

    for (var pair of data.entries()) {
      let [key, value] = pair;
      key = key.replace('[]', '');

      if (!excludedAttributes.includes(key)) {
        if (!groups[key]) {
          groups[key] = [];
        }

        groups[key].push(value);
      }
    }

    let rawData = '';

    Object.keys(groups).forEach(key => {
      groups[key] = groups[key].join('|');
      rawData += `${key}:${groups[key]}|`;
    });

    groups.rawData = rawData;

    this.pushToDataLayer({
      event: eventName,
      carSearch: groups,
    });
  }

  /**
   * Push fiksudiili search event to data layer
   * @param {object} fiksudiili fiksudiili analytics object
   */
  pushFiksudiiliSearchEvent(fiksudiili) {
    this.pushToDataLayer({
      event: 'Fiksudiili',
      fiksudiili,
    });
  }

  /**
   * Push site search's search value to data layer
   *
   * @param {string} searchInput search input as parameter
   * @public
   */
  pushSiteSearchAnalytics(searchInput) {
    this.pushToDataLayer({
      event: 'Site search',
      searchInput: searchInput,
    });
  }

  /**
   * Pushes data to Google's datalayer
   *
   * @param {object} obj object which is pushed to datalayer
   * @public
   */
  pushToDataLayer(obj) {
    if (window.dataLayer) {
      const userId = this.getUserId();

      if (userId) {
        obj.custom_client_id = userId;
      }

      window.dataLayer.push(obj);
    } else {
      console.warn('dataLayer does not exists!');
    }
  }

  getUserId() {
    const cookieName = 'gtm-user-id';
    const existingCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`))
      ?.split('=')[1];

    if (existingCookie) {
      return existingCookie;
    }

    const userId = uuidv4();
    document.cookie = `${cookieName}=${userId}; path=/; max-age=${60 * 60 * 24 * 365}; domain=.hedinautomotive.fi; SameSite=Lax`;

    return userId;
  }

  /**
   * Generic element click event handler, pushes event information from the Element to data layer.
   * @public
   * @param {Event} e click event object
   */
  handleGenericElementClick(e) {
    if (window.ANALYTIC_DEBUG) {
      console.debug('Analytics debug is on, e.stopPropagation() e.preventDefault()');
      e.preventDefault();
      e.stopPropagation();
    }

    const path = e.path || (e.composedPath && e.composedPath());
    const extbtn = path && path.find(this._findGenericElements.bind(this));

    if (extbtn && typeof extbtn.getAttribute('data-gtm-event') !== 'undefined' &&
      //Two separated events are fired from RadioGroup webcomponent, one from <label> and one <input> element inside the <label> element. Only one is needed.
      //Same happens with .hedin-radio-group element, two separated events are fired from <label> and one <input> element inside the <label> element. Only one is reported.
      !( path[0] instanceof HTMLInputElement && path.some( item => item.classList.contains('hedin-radio-group-label') ) ) &&
      !( path[0] instanceof HTMLInputElement && path.some( item => item instanceof RadioGroup ) )) {

      if (extbtn.getAttribute('data-gtm-event') === 'UI Interaction') {

        const clickText = extbtn.getAttribute('data-gtm-click-text');

        const data = {
          element: extbtn.getAttribute('data-gtm-element'),
          elementName: extbtn.getAttribute('data-gtm-name') + (clickText ? ` | Click Text: ${clickText}` : ''),
          type: extbtn.getAttribute('data-gtm-type'),
        };

        if (typeof extbtn.getAttribute('data-gtm-value') !== 'undefined' && extbtn.getAttribute('data-gtm-value') !== null) {
          data.elementValue = extbtn.getAttribute('data-gtm-value');
        }

        if (typeof extbtn.getAttribute('data-action') !== 'undefined' && extbtn.getAttribute('data-action') !== null) {
          data.action = extbtn.getAttribute('data-action');
        }

        this.pushUIInteractionAnalytics(data);
      } else if (extbtn.getAttribute('data-gtm-event') === 'Conversion') {
        let conversionId = extbtn.getAttribute('data-gtm-id');

        if (!conversionId) {
          conversionId = new Date().getTime();
        }

        this._pushConversionAnalytics({
          name: extbtn.getAttribute('data-gtm-name'),
          conversionid: conversionId,
        });
      }
    }

    //start - erppa poista kun jyrilta lupa
    let attributeSource = e.target;

    if (extbtn && typeof extbtn.getAttribute('data-category') !== 'undefined') {
      attributeSource = extbtn;
    }

    if (
      attributeSource &&
      (typeof attributeSource.getAttribute('data-category') !== 'undefined' || attributeSource.getAttribute('data-category') !== null)
    ) {
      this.pushToDataLayer({
        event: 'GAEvent',
        eventCategory: attributeSource.getAttribute('data-category'),
        eventAction: attributeSource.getAttribute('data-action') || 'click',
        eventLabel: attributeSource.getAttribute('data-label'),
        eventValue: attributeSource.getAttribute('data-value'),
      });
    }
    //end - erppa poista kun jyrilta lupa
  }

  getGenericElementClassNames() {
    return ['generic-ext-button', 'generic-button', 'generic-link', 'generic-icon-link', 'generic-clickable', 'generic-analytics'];
  }

  /**
   * If user is logged to Omalaakkonen fetch user's data from the server.
   *
   * Server side script:
   * https://laakkonen.stage.crasman.fi/admin/manage/scriptlets/scriptletmanager/scriptleteditor/site/ajax/show/w2mv73-tQZSdPVFTTEXVig
   *
   * @return {Promise} promise is resolved if user data is fetched succesfully
   *                   from the server otherwise it's rejected.
   */
  async getUser() {
    return fetch('/stage-ajax/datalayer-getuserinfo/');
  }

  /**
   * Fetch user data and put it to data layer
   *
   * NOTE: car info and page info comes from backend
   * https://laakkonen.stage.crasman.fi/admin/manage/scriptlets/scriptletmanager/scriptleteditor/site/stagemodule/show/ID1hDD_DS728_gQR9j-6qw
   */
  async _initUserAnalytics() {
    if (window.generalData) {
      try {
        const userResponse = await this.getUser();
        const user = await userResponse.json();

        const generalDataLayer = {
          event: 'General dataLayer',
          page: window.generalData.page,
        };

        const currentBrand = document.querySelector('body').dataset.brand;
        generalDataLayer.page.brandEnvironment = currentBrand === 'laakkonen' ? 'hedin' : currentBrand;

        if (window.generalData.car) {
          generalDataLayer.car = window.generalData.car;
        }

        if (user) {
          generalDataLayer.user = user;
        }

        this.pushToDataLayer(generalDataLayer);
      } catch (error) {
        console.log(error);
      }
    }
  }

  _bindLiftCard() {
    //start - erppa poista kun jyrilta lupa
    $(document).on('click', '.lift-card', function(e) {
      const self = $(this).parent('div');

      this.pushToDataLayer({
        event: 'GAEvent',
        eventCategory: 'Related content',
        eventAction: 'General content click',
        eventLabel: 'displayCategory: ' + self.attr('data-category') + ', displayBrand: ' + self.attr('data-brands'),
      });
    });
  }

  _bindPhoneNumbers() {
    $(document).on('click', 'a[href^="tel:"]', e => {
      this._pushConversionAnalytics({
        name: 'Yleinen - Puhelinnumeroa klikattu',
        conversionid: new Date().getTime(),
      });
    });
  }

  _bindMailAddresses() {
    $(document).on('click copy', 'a[href^="mailto:"]', e => {
      this._pushConversionAnalytics({
        name: 'Yleinen - Sähköposti klikattu/kopioitu',
        conversionid: new Date().getTime(),
      });
    });
  }

  _bindLekanenChat() {
    $(document).on('click', '#lekaneChat-sendButton', e => {
      this._pushConversionAnalytics({
        name: 'Yleinen - Chat-viesti lähetetty',
        conversionid: new Date().getTime(),
      });
    });
  }

  _bindTalkNowActiveContentBtn() {
    //start - erppa poista kun jyrilta lupa
    $(document).on('click', '#talkNowActiveContent .btn', function() {
      const self = $(this);

      if (self.prev('input').val() !== '') {
        this.pushToDataLayer({
          event: 'Conversion',
          conversion_type: 'soittopyynto_sent',
          'car-primary_brand': $('.single-car').attr('data-brand'),
          'car-brand': $('.single-car').attr('data-brand'),
          'car-type': $('.single-car').attr('data-category'),
        });
      }
    });
  }

  _bindCardownloadPDF() {
    $(document).on('click', '.car_download_pdf', function(e) {
      const eventLabel = $(this).hasClass('btn') ? 'Banner' : 'Text';

      this.pushToDataLayer({
        event: 'GAEvent',
        eventCategory: 'Car PDF',
        eventAction: 'Click to download',
        eventLabel: eventLabel,
        eventValue: '',
      });
    }); //end - erppa poista kun jyrilta lupa
  }

  /**
   * Bind click event listener to all HTMLelements that has a generic class name assosiated with it.
   * Generic class names are listed in `findGenericElements()` function.
   */
  _bindGenericElements() {
    document.addEventListener('click', e => {
      this.handleGenericElementClick(e);
    });
  }

  /**
   * Check if given path have any HTML elements that are considered to be generic elements.
   * @param {HTMLElement} path path to be checked for generic elements ´
   * @return {boolean} true if there is at least one element, otherwise return false.
   */
  _findGenericElements(path) {
    if (path && path.classList) {
      return this.getGenericElementClassNames().some(classname => path.classList.contains(classname));
    }
  }

  /**
   * Push conversion analytics to analytic layer
   * @param {object} data data object include name and conversionid
   */
  _pushConversionAnalytics(data) {
    this.pushToDataLayer({
      event: 'Conversion',
      conversion: {
        name: data.name,
        id: data.conversionid,
      },
    });
  }




}

export default new Analytics();
